import type { RequestTypeEnum } from "@nlc/shared-types";

import { useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect } from "react";

import { mbcSessionIds } from "@/features/request/mbc-chat/lib/session-ids";
import {
  isRequestNotFoundError,
  useGetRequest,
} from "@/hooks/useGetRequest.tsx";
import { usePageTitle } from "@/hooks/usePageTitle";

import { requestTypeEnumToString } from "../duplicate-past-request-modal/useDuplicatePastRequestForm";
import { EditRequestLayout } from "./components/EditRequestLayout";

export function EditRequestPage() {
  const { data: request, error } = useGetRequest();
  const navigate = useNavigate();
  const search = useSearch({
    from: "/_auth/_app-shell/requests/$requestId/edit",
  });
  const openInternalNotes = search["internal-notes"] === true;

  usePageTitle(request?.name?.trim() ?? "");

  useEffect(() => {
    if (isRequestNotFoundError(error))
      navigate({ replace: true, search: { filter: "all" }, to: "/requests" });
  }, [error, navigate]);

  useEffect(() => {
    if (!request?.isDraft) return;

    // `isMbc` is only ever set at creation (D6), so every pre-existing
    // `mbc-project-*` conversation — created before this column existed —
    // reads false here and falls through below (D3): no Mongo lookup, no
    // chance of landing on an empty chat for a conversation that no longer
    // resolves.
    if (request.isMbc) {
      const { resourceId, threadId } = mbcSessionIds(
        request.projectId,
        request.id,
      );

      void navigate({
        replace: true,
        search: {
          brandId: request.brandId ?? undefined,
          designType: request.designTypeId ?? undefined,
          ["project-id"]: request.projectId ?? undefined,
          resourceId,
          threadId,
        },
        to: "/requests/mbc-review",
      });

      return;
    }

    void navigate({
      replace: true,
      search: {
        draft_id: request.id,
        "request-type": requestTypeEnumToString(
          request.requestType! as RequestTypeEnum,
        ),
      },
      to: "/requests/create",
    });
  }, [request, navigate]);

  if (!request || request.isDraft) return null;

  return (
    <>
      <main className="h-full w-full bg-bg-tertiary sm:p-4 overflow-y-auto sm:overflow-hidden scrollbar-none">
        <EditRequestLayout openInternalNotes={openInternalNotes} />
      </main>
    </>
  );
}
